import type { UiTagType } from "@shared/components/UiTag/index.vue";
import type { TableRowActionItem } from "@shared/components/UiTableRowActions/index.vue";
import { t } from "@shared/utils/i18n";

export const CORP_COOPERATION_STATUS = {
    InCooperation: 1,
    PendingTermination: 2,
    Terminated: 3,
} as const;

export const CORP_COOPERATION_STATUS_OPTIONS = [
    { label: t("合作中"), value: CORP_COOPERATION_STATUS.InCooperation },
    { label: t("待解约"), value: CORP_COOPERATION_STATUS.PendingTermination },
    { label: t("已解约"), value: CORP_COOPERATION_STATUS.Terminated },
];

export const CORP_ORG_TYPE = {
    Single: 1,
    Amoeba: 2,
} as const;

export const CORP_ORG_TYPE_OPTIONS = [
    { label: t("单体"), value: CORP_ORG_TYPE.Single },
    { label: t("阿米巴"), value: CORP_ORG_TYPE.Amoeba },
];

export const CORP_CONTACT_ROLE = {
    Principal: 1,
    Finance: 2,
    Product: 3,
    Logistics: 4,
    Other: 5,
} as const;

export const CORP_CONTACT_ROLE_OPTIONS = [
    { label: t("企业负责人"), value: CORP_CONTACT_ROLE.Principal },
    { label: t("财务对接人"), value: CORP_CONTACT_ROLE.Finance },
    { label: t("产品对接人"), value: CORP_CONTACT_ROLE.Product },
    { label: t("物流对接人"), value: CORP_CONTACT_ROLE.Logistics },
    { label: t("其他"), value: CORP_CONTACT_ROLE.Other },
];

export interface CorpAddressApiItem {
    id: string;
    addressName: string;
    country: string;
    province: string;
    city: string;
    district: string;
    detailAddress: string;
}

export interface CorpContactApiItem {
    id: string;
    contactName: string;
    role: number;
    roleLabel: string;
    telephone: string;
    email: string;
}

/** 列表项（/manage/corp/list 的 items[]） */
export interface CorpListApiItem {
    id: string;
    corpCode: string;
    corpName: string;
    masterCorpId: string;
    cooperationStatus: number;
    cooperationStatusLabel: string;
    channelId?: string;
    channelName?: string;
    channelBindUserId?: string;
    channelBindAt?: string | null;
    corpShortName?: string;
    orgType?: number;
    orgTypeLabel?: string;
    adminUsername?: string;
    adminName?: string;
    primaryContactDisplay?: string;
    primaryContactTitle?: string;
    primaryContactTelephone?: string;
    addressDisplay?: string;
    addressRegion?: string;
    addressDetail?: string;
    remark?: string;
    /** 后端列表项暂未下发；列预留 */
    createdAt?: string | null;
}

/** 详情（/manage/corp/detail） */
export interface CorpDetailApiItem extends CorpListApiItem {
    addresses: CorpAddressApiItem[];
    contacts: CorpContactApiItem[];
}

export interface CorpRow {
    id: string;
    corpCode: string;
    corpName: string;
    masterCorpId: string;
    cooperationStatus: number;
    cooperationStatusLabel: string;
    channelId: string;
    channelName: string;
    channelBindAt: string;
    corpShortName: string;
    orgType: number;
    orgTypeLabel: string;
    adminUsername: string;
    adminName: string;
    adminDisplay: string;
    primaryContactDisplay: string;
    primaryContactTitle: string;
    primaryContactTelephone: string;
    addressDisplay: string;
    addressRegion: string;
    addressDetail: string;
    remark: string;
    createdAt: string;
    /** 渠道弹窗兼容：与主要联系人展示同源 */
    contactName: string;
}

export function formatCorpAdminDisplay(username?: string, name?: string): string {
    const u = (username || "").trim();
    const n = (name || "").trim();
    if ((!u || u === "-") && (!n || n === "-")) return "-";
    if (!u || u === "-") return n;
    if (!n || n === "-") return u;
    return `${u} / ${n}`;
}

/** 地址展示：省/市/区 详细地址 */
export function formatCorpAddressDisplay(address: { province?: string; city?: string; district?: string; detailAddress?: string }): string {
    const region = [address.province, address.city, address.district]
        .map(v => (v || "").trim())
        .filter(Boolean)
        .join("/");
    const detail = (address.detailAddress || "").trim();
    if (region && detail) return `${region} ${detail}`;
    return region || detail || "";
}

export function mapCorpListItemToRow(item: CorpListApiItem | CorpDetailApiItem): CorpRow {
    const primaryContactTitle = item.primaryContactTitle || "";
    const primaryContactTelephone = item.primaryContactTelephone || "";
    const primaryContactDisplay = item.primaryContactDisplay || "";
    const addressRegion = item.addressRegion || "";
    const addressDetail = item.addressDetail || "";
    return {
        id: item.id,
        corpCode: item.corpCode || "",
        corpName: item.corpName || "",
        masterCorpId: item.masterCorpId,
        cooperationStatus: item.cooperationStatus,
        cooperationStatusLabel: item.cooperationStatusLabel || "",
        channelId: item.channelId == null ? "" : String(item.channelId),
        channelName: item.channelName || "",
        channelBindAt: item.channelBindAt || "",
        corpShortName: item.corpShortName || "",
        orgType: item.orgType ?? CORP_ORG_TYPE.Single,
        orgTypeLabel: item.orgTypeLabel || "",
        adminUsername: item.adminUsername || "-",
        adminName: item.adminName || "-",
        adminDisplay: formatCorpAdminDisplay(item.adminUsername, item.adminName),
        primaryContactTitle,
        primaryContactTelephone,
        primaryContactDisplay,
        addressRegion,
        addressDetail,
        addressDisplay: item.addressDisplay || "",
        remark: item.remark || "",
        createdAt: item.createdAt || "",
        contactName: primaryContactDisplay || primaryContactTitle,
    };
}

export function getCorpCooperationStatusTagType(status: number): UiTagType {
    if (status === CORP_COOPERATION_STATUS.InCooperation) return "success";
    if (status === CORP_COOPERATION_STATUS.PendingTermination) return "error";
    if (status === CORP_COOPERATION_STATUS.Terminated) return "neutral";
    return "neutral";
}

export function getCorpListRowActions(): TableRowActionItem[] {
    return [
        { label: t("进入客户端"), key: "enterClient" },
        { label: t("详情"), key: "detail" },
        { label: t("编辑"), key: "edit" },
    ];
}
